import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import html2canvas from 'html2canvas';
import { parseAddress } from './addressFormat';

const MAP_SIZE = 600;
const DEFAULT_CENTER = { lat: 41.9028, lng: 12.4964 };
const DEFAULT_ZOOM = 16;

export type LatLng = { lat: number; lng: number };

export type GoogleMapState = {
  center: LatLng;
  zoom: number;
  mapTypeId: string;
  markerPosition: LatLng;
};

let mapsLoadPromise: Promise<void> | null = null;

export function getGoogleMapsApiKey(): string {
  return process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() ?? '';
}

export function loadGoogleMapsApi(): Promise<void> {
  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return Promise.reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY non configurata'));
  }

  if (!mapsLoadPromise) {
    setOptions({
      key: apiKey,
      v: 'weekly',
      language: 'it',
      region: 'IT',
    });
    mapsLoadPromise = Promise.all([importLibrary('maps'), importLibrary('geocoding')]).then(
      () => undefined
    );
  }

  return mapsLoadPromise;
}

/** Normalizza il formato indirizzo del form per il Geocoder Google. */
export function formatAddressForGeocoding(address: string): string {
  const { via, numero, cap, citta, provincia } = parseAddress(address);
  const street = [via, numero].filter(Boolean).join(' ');
  const place = [cap, citta].filter(Boolean).join(' ');
  let normalized = [street, place, provincia].filter(Boolean).join(', ');
  if (!normalized) normalized = address.trim();
  if (!/\b(italia|italy)\b/i.test(normalized)) {
    normalized = `${normalized}, Italia`;
  }
  return normalized;
}

function buildGeocodeCandidates(address: string): string[] {
  const trimmed = address.trim();
  const candidates: string[] = [formatAddressForGeocoding(trimmed), trimmed];

  const { via, numero, cap, citta } = parseAddress(trimmed);
  const street = [via, numero].filter(Boolean).join(' ');
  if (street && citta) {
    candidates.push(`${street}, ${citta}, Italia`);
    if (cap) {
      candidates.push(`${street}, ${cap} ${citta}, Italia`);
    }
  }

  return Array.from(new Set(candidates.filter(Boolean)));
}

function geocodeOnce(query: string): Promise<LatLng | null> {
  return new Promise(resolve => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: query, region: 'IT' }, (results, status) => {
      if (status !== 'OK' || !results?.[0]?.geometry?.location) {
        resolve(null);
        return;
      }
      const location = results[0].geometry.location;
      resolve({ lat: location.lat(), lng: location.lng() });
    });
  });
}

export async function geocodeAddress(address: string): Promise<LatLng | null> {
  await loadGoogleMapsApi();

  const trimmed = address.trim();
  if (!trimmed) return null;

  for (const candidate of buildGeocodeCandidates(trimmed)) {
    const position = await geocodeOnce(candidate);
    if (position) return position;
  }
  return null;
}

export async function captureMapAsDataUrl(
  map: google.maps.Map,
  element: HTMLElement
): Promise<string> {
  await new Promise<void>(resolve => {
    let settled = false;
    let listener: google.maps.MapsEventListener | undefined;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (listener) {
        google.maps.event.removeListener(listener);
      }
      resolve();
    };

    const timeout = window.setTimeout(finish, 1000);

    listener = google.maps.event.addListenerOnce(map, 'idle', () => {
      window.clearTimeout(timeout);
      finish();
    });

    map.panBy(0, 0);
  });

  await new Promise(resolve => window.setTimeout(resolve, 200));

  const canvas = await html2canvas(element, {
    useCORS: true,
    allowTaint: true,
    width: MAP_SIZE,
    height: MAP_SIZE,
    logging: false,
  });

  return canvas.toDataURL('image/png');
}

export { MAP_SIZE, DEFAULT_CENTER, DEFAULT_ZOOM };
